import { useEffect, useRef, useState } from 'react';
import { BiMenu } from 'react-icons/bi';
import Tippy from '@tippyjs/react';

import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';
import { displayFirstName, gigabytesToSelos } from 'components/rebrand/mappings';
import { IconSair, IconSprout } from 'components/rebrand/icons';
import { TbDoorExit } from 'react-icons/tb';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  user: any;
  userInfo: any;
  level: any;
  groupInfo: any;
  showLogout: boolean;
  onLogout: () => void;
  onOpenNavMobile: () => void;
  onSairDoPrograma: () => void;
}

/** Saldo de selos em versão reduzida, ao lado do avatar. O card completo
 *  continua no painel da Home; aqui fica só quantidade + ícone. */
function SeloBalancePill({ user }: { user: any }) {
  const selos = gigabytesToSelos(user?.point_categories?.point_behavior || 0);
  const descricao = selos === 1 ? '1 selo disponível neste ciclo' : `${selos} selos disponíveis neste ciclo`;

  return (
    // aria={{ content: null }} evita que o Tippy duplique a frase: quem usa
    // leitor de tela já a recebe pelo aria-label, com ou sem o tooltip aberto.
    <Tippy content={descricao} arrow={true} placement="bottom" aria={{ content: null, expanded: false }}>
      <div className="rb-selopill" tabIndex={0} aria-label={descricao}>
        <span className="rb-selopill__icon" aria-hidden={true}>
          <IconSprout color="#3f9a2f" size={15} />
        </span>
        <span className="rb-selopill__value">{selos}</span>
      </div>
    </Tippy>
  );
}

function ProfileRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rb-profile__row">
      <span className="rb-profile__row-label">{label}</span>
      <span className="rb-profile__row-value">{value}</span>
    </div>
  );
}

export function DashboardPageHeader({
  title,
  subtitle,
  user,
  userInfo,
  groupInfo,
  showLogout,
  onLogout,
  onOpenNavMobile,
  onSairDoPrograma,
}: PageHeaderProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="rb-pageheader" data-step="1">
      <div>
        <h1 className="rb-pageheader__title">{title}</h1>
        {subtitle && <p className="rb-pageheader__subtitle">{subtitle}</p>}
      </div>

      <div className="rb-pageheader__actions">
        <button type="button" className="rb-pageheader__menu" aria-label="Abrir menu" onClick={onOpenNavMobile}>
          <BiMenu size={28} />
        </button>

        <SeloBalancePill user={user} />

        <div ref={dropdownRef} className="rb-profile">
          <button
            type="button"
            className="rb-profile__trigger"
            aria-label="Meu perfil"
            aria-expanded={open}
            onClick={() => setOpen(prev => !prev)}
          >
            <UserAvatar data={{ ...user, ...user.extra, ariaHidden: true }} responsive={true} />
          </button>

          {open && (
            <div className="rb-profile__dropdown">
              <div className="rb-profile__identity">
                <div className="rb-profile__avatar-static" aria-hidden={true}>
                  <UserAvatar data={{ ...user, ...user.extra, ariaHidden: true }} responsive={true} />
                </div>
                <div className="rb-profile__identity-text">
                  <Humanograma data={{ ...userInfo, _id: userInfo?.chave }}>
                    <div
                      className="rb-profile__name"
                      tabIndex={0}
                      aria-label={`${displayFirstName(userInfo, user)} Chave ${user._id}`}
                    >
                      {displayFirstName(userInfo, user)}
                    </div>
                  </Humanograma>
                  <div className="rb-profile__key">({user._id})</div>
                </div>
              </div>

              <div className="rb-profile__divider" />

              <ProfileRow label="Função atual" value={userInfo?.funcao} />

              {!!groupInfo && !user?.extra?.rede && (
                <ProfileRow
                  label="Grupo"
                  value={
                    <span aria-label={`Seu grupo é ${groupInfo.nome}`} tabIndex={0}>
                      {groupInfo.nome}{' '}
                      <span className="rb-profile__group-total">
                        ({groupInfo.total} colaborador{groupInfo.total > 1 ? 'es' : ''})
                      </span>
                    </span>
                  }
                />
              )}

              <button
                type="button"
                className="rb-profile__logout rb-profile__optout"
                onClick={() => {
                  setOpen(false);
                  onSairDoPrograma();
                }}
              >
                <TbDoorExit size={17} /> Não quero mais participar
              </button>

              {showLogout && (
                <button type="button" className="rb-profile__logout" onClick={onLogout}>
                  <IconSair size={17} /> Sair
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
